import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { MapPin, RefreshCcw, Search, X } from 'lucide-react';
import {
  BorzoDeliveryRow,
  getBorzoDeliveries,
  getBorzoDeliveryLocation,
} from '../../services/MasterAdmin/borzoDeliveriesService';

import { GoogleMap, Marker, Polyline, useJsApiLoader } from '@react-google-maps/api';
import { getDirections } from '../../services/googleMapsService';
import { REACT_APP_GOOGLE_MAPS_API_KEY } from '../../config/env';

// Decode Google encoded polyline into lat/lng objects usable by Google Maps Polyline
function decodePolyline(encoded: string): google.maps.LatLngLiteral[] {
  let index = 0;
  const len = encoded.length;
  let lat = 0;
  let lng = 0;
  const coordinates: google.maps.LatLngLiteral[] = [];

  while (index < len) {
    let b = 0;
    let shift = 0;
    let result = 0;
    do {
      b = encoded.charCodeAt(index++) - 63;
      result |= (b & 0x1f) << shift;
      shift += 5;
    } while (b >= 0x20);
    const dlat = (result & 1) !== 0 ? ~(result >> 1) : result >> 1;
    lat += dlat;

    shift = 0;
    result = 0;
    do {
      b = encoded.charCodeAt(index++) - 63;
      result |= (b & 0x1f) << shift;
      shift += 5;
    } while (b >= 0x20);
    const dlng = (result & 1) !== 0 ? ~(result >> 1) : result >> 1;
    lng += dlng;

    coordinates.push({ lat: lat / 1e5, lng: lng / 1e5 });
  }

  return coordinates;
}

type TrackModalProps = {
  row: BorzoDeliveryRow;
  onClose: () => void;
};

const TrackModal: React.FC<TrackModalProps> = ({ row, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [loc, setLoc] = useState<{ lat: number; lng: number; ts: string } | null>(null);
  const [route, setRoute] = useState<{
    riderToPickup: google.maps.LatLngLiteral[];
    pickupToDrop: google.maps.LatLngLiteral[];
    totalKm: string;
    totalMin: string;
  } | null>(null);
  const timerRef = useRef<number | null>(null);
  const mapRef = useRef<google.maps.Map | null>(null);

  const pickup = useMemo(
    () => ({ lat: row.pickupLatitude, lng: row.pickupLongitude }),
    [row.pickupLatitude, row.pickupLongitude]
  );
  const drop = useMemo(
    () => ({ lat: row.deliveryLatitude, lng: row.deliveryLongitude }),
    [row.deliveryLatitude, row.deliveryLongitude]
  );

  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: REACT_APP_GOOGLE_MAPS_API_KEY || '',
  });

  const fetchLoc = useCallback(async () => {
    try {
      setError(null);
      const res = await getBorzoDeliveryLocation(row.assignmentId);
      setLoc({
        lat: res.data.latitude,
        lng: res.data.longitude,
        ts: res.data.timestamp,
      });
    } catch (e: any) {
      const status = e?.response?.status;
      const msg = e?.response?.data?.message;

      // Backend returns 404 when Firestore has no location doc yet.
      // For support UI this is not an error — it just means tracking hasn't started.
      if (status === 404) {
        setLoc(null);
        setRoute(null);
        setError(null);
        return;
      }

      setError(msg || 'Failed to fetch live location');
    } finally {
      setLoading(false);
    }
  }, [row.assignmentId]);

  const fetchRoute = useCallback(
    async (riderLatLng: { lat: number; lng: number } | null) => {
      try {
        const origin = riderLatLng
          ? `${riderLatLng.lat},${riderLatLng.lng}`
          : `${pickup.lat},${pickup.lng}`;
        const pickupStr = `${pickup.lat},${pickup.lng}`;
        const dropStr = `${drop.lat},${drop.lng}`;

        const res = await getDirections({ origin, pickup: pickupStr, drop: dropStr });
        if (!res?.success) return;

        const o2p = res.data.originToPickup?.polyline || '';
        const p2d = res.data.pickupToDrop?.polyline || '';
        const riderToPickup = o2p ? decodePolyline(o2p) : [];
        const pickupToDrop = p2d ? decodePolyline(p2d) : [];

        setRoute({
          riderToPickup,
          pickupToDrop,
          totalKm: res.data.summary?.totalDistance?.kilometers || '0',
          totalMin: res.data.summary?.totalDuration?.minutes || '0',
        });
      } catch (e) {
        // Non-blocking; tracking still works without route
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [row.assignmentId, pickup.lat, pickup.lng, drop.lat, drop.lng]
  );

  useEffect(() => {
    fetchLoc();
    fetchRoute(null);
    timerRef.current = window.setInterval(async () => {
      await fetchLoc();
      // Use the latest loc state after fetchLoc finishes on next tick
    }, 5000);
    return () => {
      if (timerRef.current) window.clearInterval(timerRef.current);
    };
  }, [fetchLoc, fetchRoute]);

  // When rider location changes, refresh route (throttled by location polling cadence)
  useEffect(() => {
    if (!loc) return;
    fetchRoute({ lat: loc.lat, lng: loc.lng });
  }, [loc?.lat, loc?.lng, fetchRoute]); // eslint-disable-line react-hooks/exhaustive-deps

  const rider = loc ? ({ lat: loc.lat, lng: loc.lng } as google.maps.LatLngLiteral) : null;

  // Fit map to markers whenever they change
  useEffect(() => {
    if (!isLoaded) return;
    if (!mapRef.current) return;

    const bounds = new google.maps.LatLngBounds();
    bounds.extend(pickup);
    bounds.extend(drop);
    if (rider) bounds.extend(rider);
    mapRef.current.fitBounds(bounds, 60);
  }, [isLoaded, pickup, drop, rider]);

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center overflow-y-auto bg-black/40 p-2 pb-[max(0.5rem,env(safe-area-inset-bottom))] sm:items-center sm:p-4">
      <div className="w-full max-w-5xl overflow-hidden rounded-t-xl bg-white shadow-xl dark:bg-gray-800 dark:shadow-gray-900/30 sm:rounded-xl">
        <div className="flex items-center justify-between border-b px-5 py-4 dark:border-gray-700">
          <div>
            <div className="font-semibold text-gray-900 dark:text-white">
              {row.order?.orderId} • {row.externalOrderId || row.order?.borzoOrderId || '—'}
            </div>
            <div className="text-sm text-gray-500 dark:text-gray-400">
              {row.order?.status} • {row.status}
            </div>
          </div>
          <button
            onClick={onClose}
            className="rounded-md p-2 text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="grid grid-cols-1 gap-4 p-5 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <div className="h-[min(520px,55vh)] min-h-[220px] overflow-hidden rounded-lg border dark:border-gray-700 sm:h-[520px]">
              {!isLoaded ? (
                <div className="flex h-full w-full items-center justify-center text-sm text-gray-500 dark:text-gray-400">
                  Loading Google Maps…
                </div>
              ) : !REACT_APP_GOOGLE_MAPS_API_KEY ? (
                <div className="flex h-full w-full items-center justify-center p-6 text-center text-sm text-red-600">
                  Missing <code className="mx-1 rounded bg-red-50 px-1 py-0.5">REACT_APP_GOOGLE_MAPS_API_KEY</code> in admin panel env.
                </div>
              ) : (
                <GoogleMap
                  mapContainerStyle={{ height: '100%', width: '100%' }}
                  center={pickup}
                  zoom={13}
                  options={{
                    fullscreenControl: false,
                    streetViewControl: false,
                    mapTypeControl: false,
                    clickableIcons: false,
                  }}
                  onLoad={(map) => {
                    mapRef.current = map;
                  }}
                  onUnmount={() => {
                    mapRef.current = null;
                  }}
                >
                  <Marker position={pickup} label="P" />
                  <Marker position={drop} label="D" />
                  {rider ? <Marker position={rider} label="R" /> : null}

                  {/* Route from rider(origin) → pickup → drop (Google Directions) */}
                  {route?.riderToPickup?.length ? (
                    <Polyline
                      path={route.riderToPickup}
                      options={{ strokeColor: '#ef4444', strokeOpacity: 0.95, strokeWeight: 4 }}
                    />
                  ) : null}
                  {route?.pickupToDrop?.length ? (
                    <Polyline
                      path={route.pickupToDrop}
                      options={{ strokeColor: '#16a34a', strokeOpacity: 0.85, strokeWeight: 4 }}
                    />
                  ) : null}
                </GoogleMap>
              )}
            </div>
          </div>

          <div className="space-y-3">
            <div className="rounded-lg border p-4 dark:border-gray-700">
              <div className="text-xs font-semibold uppercase text-gray-500 dark:text-gray-400">Status</div>
              <div className="mt-2 space-y-1 text-sm text-gray-900 dark:text-gray-100">
                <div>
                  <span className="text-gray-500 dark:text-gray-400">Order:</span>{' '}
                  <span className="font-medium">{row.order?.status}</span>
                </div>
                <div>
                  <span className="text-gray-500 dark:text-gray-400">Assignment:</span>{' '}
                  <span className="font-medium">{row.status}</span>
                </div>
                <div>
                  <span className="text-gray-500 dark:text-gray-400">Courier:</span>{' '}
                  <span className="font-medium">
                    {row.order?.borzoCourierName || row.externalCourierName || '—'}
                  </span>
                </div>
              </div>
            </div>

            <div className="rounded-lg border p-4 dark:border-gray-700">
              <div className="text-xs font-semibold uppercase text-gray-500 dark:text-gray-400">Live location</div>
              <div className="mt-2 text-sm text-gray-900 dark:text-gray-100">
                {loading ? (
                  <div className="text-gray-500 dark:text-gray-400">Loading…</div>
                ) : error ? (
                  <div className="text-red-600">{error}</div>
                ) : loc ? (
                  <div className="space-y-1">
                    {route ? (
                      <div className="mb-2 rounded-md bg-gray-50 p-2 text-xs text-gray-700 dark:bg-gray-700 dark:text-gray-200">
                        Route (Google): ~{route.totalKm} km • ~{route.totalMin} min
                      </div>
                    ) : null}
                    <div>
                      <span className="text-gray-500 dark:text-gray-400">Lat/Lng:</span>{' '}
                      <span className="font-medium">
                        {loc.lat.toFixed(6)}, {loc.lng.toFixed(6)}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-500 dark:text-gray-400">Updated:</span>{' '}
                      <span className="font-medium">{new Date(loc.ts).toLocaleString()}</span>
                    </div>
                    <button
                      onClick={fetchLoc}
                      className="mt-2 inline-flex items-center gap-2 rounded-md border px-3 py-2 text-sm hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-gray-700"
                    >
                      <RefreshCcw className="h-4 w-4" />
                      Refresh now
                    </button>
                  </div>
                ) : (
                  <div className="text-gray-500 dark:text-gray-400">No location yet</div>
                )}
              </div>
            </div>

            <div className="rounded-lg border p-4 dark:border-gray-700">
              <div className="text-xs font-semibold uppercase text-gray-500 dark:text-gray-400">Addresses</div>
              <div className="mt-2 space-y-2 text-sm text-gray-900 dark:text-gray-100">
                <div>
                  <div className="text-gray-500 dark:text-gray-400">Pickup</div>
                  <div className="font-medium">{row.pickupAddress || row.order?.vendor?.address || '—'}</div>
                </div>
                <div>
                  <div className="text-gray-500 dark:text-gray-400">Drop</div>
                  <div className="font-medium">
                    {row.deliveryAddress || row.order?.deliveryAddress?.formattedAddress || '—'}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const BorzoDeliveriesPage: React.FC = () => {
  const [rows, setRows] = useState<BorzoDeliveryRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [search, setSearch] = useState('');
  const [orderStatus, setOrderStatus] = useState<string>('ALL');
  const [assignmentStatus, setAssignmentStatus] = useState<string>('ALL');
  const [fromDate, setFromDate] = useState<string>('');
  const [toDate, setToDate] = useState<string>('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedToTrack, setSelectedToTrack] = useState<BorzoDeliveryRow | null>(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await getBorzoDeliveries({
        search: search.trim() || undefined,
        orderStatus: orderStatus === 'ALL' ? undefined : orderStatus,
        assignmentStatus: assignmentStatus === 'ALL' ? undefined : assignmentStatus,
        from: fromDate ? new Date(fromDate).toISOString() : undefined,
        to: toDate ? new Date(`${toDate}T23:59:59.999`).toISOString() : undefined,
        page,
        limit: 50,
      });
      setRows(res.data || []);
      setTotalPages(res.pagination?.totalPages || 1);
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Failed to load Borzo deliveries');
      setRows([]);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  }, [search, orderStatus, assignmentStatus, fromDate, toDate, page]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (page !== 1) setPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, orderStatus, assignmentStatus, fromDate, toDate]);

  const statusBadge = (s: string) => {
    const v = String(s || '').toLowerCase();
    const base = 'inline-flex items-center rounded-full px-2 py-1 text-xs font-semibold';
    if (['delivered', 'completed', 'finished'].includes(v))
      return `${base} bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300`;
    if (['cancelled', 'canceled'].includes(v))
      return `${base} bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300`;
    if (['out_for_delivery', 'active', 'in_transit', 'picked_up'].includes(v))
      return `${base} bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300`;
    return `${base} bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200`;
  };

  return (
    <div className="w-full min-w-0 space-y-4">
      {/* Filters only (page title is handled by MainLayout header) */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow dark:shadow-gray-900/30 overflow-hidden">
        <div className="p-4 border-b dark:border-gray-700">
          {/* Horizontal scroll for filters on smaller widths */}
          <div className="overflow-x-auto">
            <div className="flex min-w-max flex-nowrap items-center gap-2">
              <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search orderId / borzoOrderId"
                  className="w-[min(100vw-4rem,320px)] max-w-full rounded-lg border border-gray-300 px-9 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                />
              </div>

              <select
                value={orderStatus}
                onChange={(e) => setOrderStatus(e.target.value)}
                className="rounded-lg border border-gray-300 px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-700 dark:text-white"
              >
                <option value="ALL">All order statuses</option>
                <option value="ready_to_pickup">ready_to_pickup</option>
                <option value="out_for_delivery">out_for_delivery</option>
                <option value="delivered">delivered</option>
                <option value="cancelled">cancelled</option>
                <option value="cancelled_before_pickup">cancelled_before_pickup</option>
                <option value="payment_completed">payment_completed</option>
              </select>

              <select
                value={assignmentStatus}
                onChange={(e) => setAssignmentStatus(e.target.value)}
                className="rounded-lg border border-gray-300 px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-700 dark:text-white"
              >
                <option value="ALL">All assignment statuses</option>
                <option value="pending">pending</option>
                <option value="accepted">accepted</option>
                <option value="picked_up">picked_up</option>
                <option value="in_transit">in_transit</option>
                <option value="delivered">delivered</option>
                <option value="cancelled">cancelled</option>
              </select>

              <input
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                className="rounded-lg border border-gray-300 px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                title="From date"
              />
              <input
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                className="rounded-lg border border-gray-300 px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                title="To date"
              />

              <button
                onClick={fetchData}
                className="inline-flex items-center justify-center gap-2 rounded-lg bg-primary-600 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-700"
              >
                <RefreshCcw className="h-4 w-4" />
                Refresh
              </button>
            </div>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow dark:shadow-gray-900/30 overflow-hidden p-6 text-sm text-gray-500 dark:text-gray-400">Loading…</div>
      ) : error ? (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow dark:shadow-gray-900/30 overflow-hidden p-6 text-sm text-red-600">{error}</div>
      ) : (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow dark:shadow-gray-900/30 overflow-hidden">
          <div className="max-w-full overflow-x-auto scrollbar-light">
            <table className="min-w-max w-full text-left text-sm">
              <thead className="bg-gray-50 dark:bg-gray-700 text-xs uppercase text-gray-500 dark:text-gray-200">
                <tr>
                  <th className="px-4 py-3">Order</th>
                  <th className="px-4 py-3">Borzo</th>
                  <th className="px-4 py-3">Order status</th>
                  <th className="px-4 py-3">Assignment status</th>
                  <th className="px-4 py-3">Vendor</th>
                  <th className="px-4 py-3">Customer</th>
                  <th className="px-4 py-3">Charge</th>
                  <th className="px-4 py-3">Created</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {rows.map((r) => (
                  <tr key={r.assignmentId} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                    <td className="px-4 py-3 text-gray-900 dark:text-gray-100">
                      <div className="font-medium">{r.order?.orderId}</div>
                    </td>
                    <td className="px-4 py-3 text-gray-900 dark:text-gray-100">
                      <div className="font-medium">{r.externalOrderId || r.order?.borzoOrderId || '—'}</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">{r.externalDeliveryId || r.order?.borzoDeliveryId || ''}</div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={statusBadge(r.order?.status)}>{r.order?.status}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={statusBadge(r.status)}>{r.status}</span>
                    </td>
                    <td className="px-4 py-3 text-gray-900 dark:text-gray-100">{r.order?.vendor?.name || '—'}</td>
                    <td className="px-4 py-3 text-gray-900 dark:text-gray-100">
                      <div className="font-medium">{r.order?.user?.name || '—'}</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">{r.order?.user?.phone_number || ''}</div>
                    </td>
                    <td className="px-4 py-3 text-gray-900 dark:text-gray-100">₹ {Number(r.deliveryCharges || r.order?.deliveryCharges || 0).toFixed(2)}</td>
                    <td className="px-4 py-3 text-gray-900 dark:text-gray-100">
                      {r.createdAt ? new Date(r.createdAt).toLocaleString() : '—'}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => setSelectedToTrack(r)}
                        className="rounded-lg border border-gray-300 px-3 py-2 text-sm font-semibold hover:bg-gray-50 dark:border-gray-600 dark:text-white dark:hover:bg-gray-700"
                      >
                        Track on map
                      </button>
                    </td>
                  </tr>
                ))}
                {rows.length === 0 && (
                  <tr>
                    <td className="px-4 py-8 text-center text-sm text-gray-500 dark:text-gray-400" colSpan={9}>
                      No Borzo deliveries found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="flex flex-col gap-3 border-t px-4 py-3 text-sm dark:border-gray-700 sm:flex-row sm:items-center sm:justify-between">
            <div className="text-gray-500 dark:text-gray-400">
              Page {page} of {totalPages}
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(p - 1, 1))}
                className="rounded-lg border border-gray-300 px-3 py-2 disabled:opacity-50 dark:border-gray-600 dark:text-white dark:hover:bg-gray-700"
              >
                Prev
              </button>
              <button
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
                className="rounded-lg border border-gray-300 px-3 py-2 disabled:opacity-50 dark:border-gray-600 dark:text-white dark:hover:bg-gray-700"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      )}

      {selectedToTrack && (
        <TrackModal row={selectedToTrack} onClose={() => setSelectedToTrack(null)} />
      )}
    </div>
  );
};

export default BorzoDeliveriesPage;

